import { useEffect, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";

// Debug (remove later if you want)
console.log("ENV CHECK", {
  api: process.env.REACT_APP_API_URL,
  pk: process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY?.slice(0, 12),
});

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5001";
const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

if (!STRIPE_PUBLISHABLE_KEY) {
  console.error(
    "Missing REACT_APP_STRIPE_PUBLISHABLE_KEY in environment. Stripe.js cannot be initialised."
  );
}

const stripePromise = STRIPE_PUBLISHABLE_KEY
  ? loadStripe(STRIPE_PUBLISHABLE_KEY)
  : null;

function authHeaders() {
  const token = localStorage.getItem("accessToken");
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

/* --------------------------------------------------------
   Payment Form Component
-------------------------------------------------------- */
function PayForm() {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [elementReady, setElementReady] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) return;

    if (!elementReady) {
      setError("The payment form is still loading. Please wait and try again.");
      return;
    }

    setLoading(true);
    setError("");

    const { error: stripeError } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/tenant-portal?paid=1`,
      },
    });

    if (stripeError) {
      console.error("Stripe confirmPayment error:", stripeError);
      setError(stripeError.message || "Payment failed. Please try again.");
    }

    setLoading(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 max-w-md">
      <PaymentElement
        onReady={() => {
          setElementReady(true);
          setError("");
        }}
      />

      {!elementReady && !loading && (
        <p className="text-xs text-gray-500">Loading secure payment form…</p>
      )}

      {error && <p className="text-sm text-red-600">{error}</p>}

      <button
        type="submit"
        disabled={!stripe || !elements || !elementReady || loading}
        className="px-4 py-2 rounded bg-blue-600 text-white disabled:bg-gray-400 disabled:cursor-not-allowed"
      >
        {loading ? "Processing…" : "Pay now"}
      </button>
    </form>
  );
}

/* --------------------------------------------------------
   Main Component
-------------------------------------------------------- */
export default function RentPayNow({ tenancyId, invoiceId }) {
  const [clientSecret, setClientSecret] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    async function createIntent() {
      try {
        setError("");

        const res = await fetch(`${API_BASE}/api/rent/pay-now`, {
          method: "POST",
          headers: authHeaders(),
          credentials: "include",
          body: JSON.stringify({
            tenancyId,
            invoiceId,
          }),
        });

        if (!res.ok) {
          const text = await res.text();
          throw new Error(
            `Failed to create payment intent (${res.status}): ${text}`
          );
        }

        const data = await res.json();

        if (!data.clientSecret) {
          if (data.mode === "mock") {
            throw new Error(
              "Backend is in MOCK mode (missing STRIPE_SECRET_KEY) — cannot create real payments."
            );
          }
          throw new Error("Server did not return a clientSecret.");
        }

        setClientSecret(data.clientSecret);
      } catch (err) {
        console.error("Error creating rent payment intent:", err);
        setError(err.message || "Unable to start payment");
      }
    }

    if (tenancyId) {
      createIntent();
    } else {
      setError("Missing tenancy.");
    }
  }, [tenancyId, invoiceId]);

  if (!STRIPE_PUBLISHABLE_KEY || !stripePromise) {
    return (
      <div className="text-sm text-red-600">
        Stripe is not configured. Please contact the property manager.
      </div>
    );
  }

  if (error && !clientSecret) {
    return <div className="text-sm text-red-600">{error}</div>;
  }

  if (!clientSecret) {
    return <div>Loading payment…</div>;
  }

  return (
    <Elements stripe={stripePromise} options={{ clientSecret }}>
      <PayForm />
    </Elements>
  );
}
